import React, { useEffect } from 'react';
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import { Grid } from '@material-ui/core';
import qs from 'query-string';

import { useCurrentUser } from '@klp/auth';
import { Client } from '@klp/client/classes';
import { Conditional } from '@klp/conditional';
import { AppRoutes } from '@klp/core/app/config';
import { useAppParams } from '@klp/core/app/useAppParams';
import { ActionButton, Block, Heading } from '@klp/design';
import { Loader } from '@klp/design/Loader';
import { ErrorNotification } from '@klp/errorNotification';
import { Main } from '@klp/layout';

import {
    useGetUserClientsQuery,
    useSelectClientMutation,
    useResetClientMutation,
} from '../rtkApi';

import { ClientSelectionTables } from './ClientSelectionTables';

export const ClientSelector: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const { selectedClient } = useAppParams();
    const { currentUser, isAdmin, isAuthenticated } = useCurrentUser();

    const [selectClient] = useSelectClientMutation();
    const [resetClient] = useResetClientMutation();

    const { data: clients = [], isLoading: loading } = useGetUserClientsQuery(
        {
            includeInActive: true,
        },
        {
            skip: !isAuthenticated || !currentUser,
        },
    );

    const kundeNummer = selectedClient?.kundeNummer;

    useEffect(() => {
        if (selectedClient) {
            selectClient(Client.parseObject(selectedClient));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kundeNummer]);

    const handleSelectClientReset = () => resetClient();

    const handleSelectClient = (client: Client) => {
        selectClient(client);
        const returnUrl = qs.parse(location.search).returnUrl;
        if (returnUrl) {
            history.push(returnUrl.toString());
        } else {
            history.push(AppRoutes.mainPage());
        }
    };

    const returnUrl = qs.parse(location.search).returnUrl;
    const hasNoClients = clients.length === 0;
    const hasOneClient = clients.length === 1;

    return (
        <Main>
            <Loader isLoading={loading}>
                <Conditional
                    condition={!hasNoClients}
                    errorComponent={
                        <ErrorNotification
                            title="clientSelect.errorNoClients.title"
                            description="clientSelect.errorNoClients.description"
                            backTarget={AppRoutes.mainPage()}
                            backTitle="general.goBackToFrontpage"
                            data-testid="ClientSelector__NoClients"
                        />
                    }
                >
                    {hasOneClient && (
                        <Redirect
                            to={
                                returnUrl
                                    ? returnUrl.toString()
                                    : AppRoutes.mainPage()
                            }
                        />
                    )}

                    <div className="ClientSelect">
                        <Heading
                            klpHeadingLevel="THREE"
                            translation="clientSelect.chooseClient"
                            marginBottom="compact"
                        />
                        {isAdmin && selectedClient && (
                            <Grid container={true}>
                                <Grid item={true} xs={12}>
                                    <Block marginBottom="bulky">
                                        <ActionButton
                                            actionType="cancel"
                                            translation="clientSelect.selectedClient"
                                            translationOptions={{
                                                customerName:
                                                    selectedClient.kundeName,
                                            }}
                                            onClick={handleSelectClientReset}
                                            data-testid="ClientSelectClearSelection"
                                            clickHandler={
                                                handleSelectClientReset
                                            }
                                        />
                                    </Block>
                                </Grid>
                            </Grid>
                        )}
                        <ClientSelectionTables
                            clients={clients}
                            onSelectClient={handleSelectClient}
                            showInactive={isAdmin}
                        />
                    </div>
                </Conditional>
            </Loader>
        </Main>
    );
};
